import React, { useEffect, useMemo, useState } from "react";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Printer,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import api from "../../lib/api";
import { toast } from "sonner";
import { useT } from "../../context/LanguageContext";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const firstOfYearYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const fmtUSD = (n) =>
  (typeof n === "number" ? n : 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const triggerBrowserDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

const ExportCsvDialog = ({ open, onClose, t }) => {
  const [period, setPeriod] = useState("ytd");
  const [start, setStart] = useState(firstOfYearYmd());
  const [end, setEnd] = useState(todayYmd());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPeriod("ytd");
      setStart(firstOfYearYmd());
      setEnd(todayYmd());
      setBusy(false);
    }
  }, [open]);

  const download = async () => {
    if (period === "custom" && (!start || !end)) {
      toast.error(t("fin.exportErrorRange"));
      return;
    }
    setBusy(true);
    try {
      const params = { format: "csv", period };
      if (period === "custom") {
        params.start_date = start;
        params.end_date = end;
      }
      const res = await api.get("/financials/export", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      // Pull filename from Content-Disposition if present.
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `tasklio_summary_${period}.csv`;
      triggerBrowserDownload(blob, fname);
      toast.success(t("fin.savedToast"));
      onClose();
    } catch (e) {
      toast.error(t("fin.exportError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="export-csv-dialog">
        <DialogHeader>
          <DialogTitle>{t("fin.exportDialogTitle")}</DialogTitle>
          <DialogDescription>{t("fin.exportDialogBody")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { val: "this_month", k: "fin.periodThisMonth" },
              { val: "ytd", k: "fin.periodYtd" },
              { val: "custom", k: "fin.periodCustom" },
            ].map((p) => (
              <button
                key={p.val}
                type="button"
                onClick={() => setPeriod(p.val)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  period === p.val
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-gray-200 bg-white text-gray-700 hover:border-emerald-200 hover:bg-emerald-50/40"
                }`}
                data-testid={`export-period-${p.val}`}
              >
                {t(p.k)}
              </button>
            ))}
          </div>
          {period === "custom" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp-start">{t("fin.customStart")}</Label>
                <Input
                  id="exp-start"
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  data-testid="export-start"
                />
              </div>
              <div>
                <Label htmlFor="exp-end">{t("fin.customEnd")}</Label>
                <Input
                  id="exp-end"
                  type="date"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  data-testid="export-end"
                />
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="export-cancel">
            {t("fin.cancel")}
          </Button>
          <Button
            type="button"
            onClick={download}
            disabled={busy}
            className="bg-green-800 hover:bg-green-900 text-white"
            data-testid="export-download"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("fin.exporting")}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                {t("fin.downloadCsv")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const YearEndDialog = ({ open, onClose, t }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setData(null);
    api
      .get("/dashboard/year-end-summary")
      .then((r) => setData(r.data))
      .catch(() => toast.error(t("fin.exportError")))
      .finally(() => setLoading(false));
  }, [open, t]);

  const categoryLabel = (cat) => {
    // Reuse the existing fin.cat.<key> i18n keys; falls back to the raw
    // value when an unexpected category appears.
    const key = `fin.cat.${cat}`;
    const translated = t(key);
    return translated === key ? cat : translated;
  };

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    triggerBrowserDownload(blob, `tasklio_year_end_${data.year}.json`);
    toast.success(t("fin.savedToast"));
  };

  const totalCategoryAmount = useMemo(
    () =>
      (data?.top_expense_categories || []).reduce(
        (a, b) => a + (b.total || 0),
        0,
      ) || 0,
    [data],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto print:max-w-full print:max-h-none print:overflow-visible"
        data-testid="year-end-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {t("fin.yearEndTitle")}
            {data?.year ? <span className="text-gray-400 ml-2">· {data.year}</span> : null}
          </DialogTitle>
          <DialogDescription>{t("fin.yearEndSubtitle")}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {t("fin.loading")}
          </div>
        ) : !data ? (
          <div className="py-10 text-center text-gray-500" data-testid="year-end-empty">
            {t("fin.yearEndEmpty")}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Top metric cards — printable */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="year-end-cards">
              {[
                { label: t("fin.totalRevenue"), value: fmtUSD(data.revenue_total), tone: "emerald", testid: "yec-revenue" },
                { label: t("fin.totalExpenses"), value: fmtUSD(data.expense_total), tone: "red", testid: "yec-expenses" },
                { label: t("fin.estimatedNet"), value: fmtUSD(data.estimated_net), tone: data.estimated_net >= 0 ? "emerald" : "red", testid: "yec-net" },
                { label: t("fin.closedJobs"), value: String(data.closed_jobs_count || 0), tone: "emerald", testid: "yec-closed" },
                { label: t("fin.cancelledJobsLabel"), value: String(data.cancelled_jobs_count || 0), tone: "slate", testid: "yec-cancelled-jobs" },
                { label: t("fin.cancelledRevenue"), value: fmtUSD(data.cancelled_revenue), tone: "slate", testid: "yec-cancelled-rev" },
              ].map((c) => (
                <div
                  key={c.testid}
                  className="rounded-xl border border-gray-200 bg-white p-4"
                  data-testid={c.testid}
                >
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                    {c.label}
                  </p>
                  <p className={`mt-1 font-display text-xl font-semibold ${c.tone === "emerald" ? "text-emerald-700" : c.tone === "red" ? "text-red-700" : "text-slate-900"}`}>
                    {c.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Top categories */}
            <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="year-end-categories">
              <h3 className="font-display text-base font-semibold text-gray-900 mb-3">
                {t("fin.topCategoriesTitle")}
              </h3>
              {data.top_expense_categories && data.top_expense_categories.length > 0 ? (
                <ul className="space-y-2">
                  {data.top_expense_categories.map((row) => {
                    const pct = totalCategoryAmount > 0 ? Math.round((row.total / totalCategoryAmount) * 100) : 0;
                    return (
                      <li
                        key={row.category}
                        className="flex items-center gap-3"
                        data-testid={`yec-cat-${row.category}`}
                      >
                        <span className="w-32 text-sm text-gray-700 truncate">
                          {categoryLabel(row.category)}
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-24 text-right">
                          {fmtUSD(row.total)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">{t("fin.yearEndEmpty")}</p>
              )}
            </div>

            <p className="text-xs text-gray-500 italic">
              {t("fin.estimateBadge")}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 print:hidden">
          <Button variant="outline" onClick={onClose} data-testid="year-end-close">
            {t("fin.close")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => window.print()}
            data-testid="year-end-print"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            {t("fin.yearEndPrint")}
          </Button>
          <Button
            type="button"
            onClick={downloadJson}
            disabled={!data}
            className="bg-green-800 hover:bg-green-900 text-white"
            data-testid="year-end-download-json"
          >
            <FileText className="w-4 h-4 mr-1.5" />
            {t("fin.downloadJson")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FinancialsExports = () => {
  const { t } = useT();
  const [csvOpen, setCsvOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="fin-exports">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mr-1">
        {t("fin.exportsTitle")}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCsvOpen(true)}
        className="border-emerald-200 bg-white text-emerald-900 hover:bg-emerald-50"
        data-testid="fin-export-csv"
      >
        <FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-700" />
        {t("fin.exportCsv")}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setYearOpen(true)}
        className="border-emerald-200 bg-white text-emerald-900 hover:bg-emerald-50"
        data-testid="fin-year-end"
      >
        <CalendarIcon className="w-4 h-4 mr-1.5 text-emerald-700" />
        {t("fin.downloadYearEnd")}
      </Button>

      <ExportCsvDialog open={csvOpen} onClose={() => setCsvOpen(false)} t={t} />
      <YearEndDialog open={yearOpen} onClose={() => setYearOpen(false)} t={t} />
    </div>
  );
};

export default FinancialsExports;
